import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  listBusinesses, registerBusiness, getBusiness, updateBusiness, deleteBusiness, 
  listKeywords, addTrackedKeywords, deleteKeyword,
  refreshKeywordSearch, refreshGridSearch, refreshSeoAudit, refreshTechnicalHealth,
  refreshContentQuality, refreshAeoSignals, refreshKeywordGeneration, refreshPerspectiveSearch,
  getLatestSnapshot,
  Business as BusinessType, Keyword
} from '../lib/api';
import { Plus, Trash2, Edit, Save, X, RefreshCw, Loader2, Search, MapPin, Globe, Activity, Settings, FileJson, ChevronDown, ChevronUp, ArrowLeft } from 'lucide-react';
import MapPicker from '../components/MapPicker';
import { 
  SeoAuditRenderer, 
  KeywordSearchRenderer, 
  KeywordGenerationRenderer, 
  GridSearchRenderer, 
  PerspectiveSearchRenderer, 
  TechnicalHealthRenderer, 
  GenericBlockRenderer 
} from '../components/BlockRenderers';

const BLOCKS = [
  'keyword_search', 'keyword_generation', 'grid_search', 'perspective_search',
  'seo_audit', 'technical_health', 'content_quality', 'advanced_aeo_signals'
] as const;

export default function Businesses() {
  const queryClient = useQueryClient();
  const [selectedBusinessId, setSelectedBusinessId] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<BusinessType>>({});
  const [newKeyword, setNewKeyword] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  const [triggerModal, setTriggerModal] = useState<{ open: boolean; block: string }>({ open: false, block: '' });
  const [triggerForm, setTriggerForm] = useState<any>({});

  const { data: businessesList, isLoading: listLoading } = useQuery({
    queryKey: ['businesses-list', searchQuery],
    queryFn: () => listBusinesses(1, 50, searchQuery || undefined),
    enabled: !selectedBusinessId,
  });

  const { data: business, isLoading: detailLoading } = useQuery({
    queryKey: ['business', selectedBusinessId],
    queryFn: () => getBusiness(selectedBusinessId!),
    enabled: !!selectedBusinessId,
  });

  const { data: keywords } = useQuery({
    queryKey: ['keywords', selectedBusinessId],
    queryFn: () => listKeywords(selectedBusinessId!),
    enabled: !!selectedBusinessId,
  });

  const updateMutation = useMutation({
    mutationFn: (payload: any) => updateBusiness(selectedBusinessId!, payload),
    onSuccess: (data) => {
      setEditForm(data);
      setIsEditing(false);
      queryClient.invalidateQueries({ queryKey: ['business', selectedBusinessId] });
      queryClient.invalidateQueries({ queryKey: ['businesses-list'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteBusiness(selectedBusinessId!),
    onSuccess: () => {
      setSelectedBusinessId(null);
      queryClient.invalidateQueries({ queryKey: ['businesses-list'] });
    },
  });

  const addKeywordMutation = useMutation({
    mutationFn: () => addTrackedKeywords(selectedBusinessId!, [newKeyword]),
    onSuccess: () => {
      setNewKeyword('');
      queryClient.invalidateQueries({ queryKey: ['keywords', selectedBusinessId] });
    },
  });

  const deleteKeywordMutation = useMutation({
    mutationFn: (kwId: string) => deleteKeyword(selectedBusinessId!, kwId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['keywords', selectedBusinessId] }),
  });

  const triggerMutation = useMutation({
    mutationFn: (action: () => Promise<any>) => action(),
    onSuccess: () => {
      setTriggerModal({ open: false, block: '' });
      setTriggerForm({});
      if (selectedBusinessId) {
         queryClient.invalidateQueries({ queryKey: ['snapshot', selectedBusinessId] });
      }
    },
  });

  const handleAddKeyword = (e: React.FormEvent) => {
    e.preventDefault();
    if (newKeyword.trim()) addKeywordMutation.mutate();
  };

  const openTriggerModal = (block: string) => {
    setTriggerModal({ open: true, block });
    if (block === 'perspective_search') {
      setTriggerForm({ keyword: '', points: [{ lat: business?.lat || 0, lng: business?.lng || 0, name: 'Point 1' }] });
    } else if (block === 'grid_search') {
      setTriggerForm({ keyword: '', grid_size: 3, radius_km: 3 });
    } else {
      setTriggerForm({});
    }
  };

  const executeTrigger = () => {
    const { block } = triggerModal;
    if (block === 'grid_search') {
      triggerMutation.mutate(() => refreshGridSearch(selectedBusinessId!, triggerForm.keyword, triggerForm.grid_size, triggerForm.radius_km));
    } else if (block === 'perspective_search') {
      triggerMutation.mutate(() => refreshPerspectiveSearch(selectedBusinessId!, triggerForm));
    } else if (block === 'keyword_generation') {
      triggerMutation.mutate(() => refreshKeywordGeneration(selectedBusinessId!, triggerForm.validate_top || 0));
    } else if (block === 'seo_audit') {
      triggerMutation.mutate(() => refreshSeoAudit(selectedBusinessId!, triggerForm.page_cap || 50));
    } else if (block === 'technical_health') {
      triggerMutation.mutate(() => refreshTechnicalHealth(selectedBusinessId!, triggerForm.strategy || 'mobile'));
    } else if (block === 'keyword_search') {
      triggerMutation.mutate(() => refreshKeywordSearch(selectedBusinessId!));
    } else if (block === 'content_quality') {
      triggerMutation.mutate(() => refreshContentQuality(selectedBusinessId!));
    } else if (block === 'advanced_aeo_signals') {
      triggerMutation.mutate(() => refreshAeoSignals(selectedBusinessId!));
    }
  };

  if (listLoading || detailLoading) {
    return <div className="flex items-center justify-center h-64 text-slate-500"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading...</div>;
  }

  // --- LIST VIEW ---
  if (!selectedBusinessId && businessesList) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-slate-100">Businesses</h1>
          <button 
            onClick={() => setSelectedBusinessId('NEW')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-md transition-colors"
          >
            <Plus className="h-4 w-4" /> Register New
          </button>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by business name..."
            className="w-full bg-slate-900 border border-slate-700 rounded-md pl-10 pr-4 py-2 text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-slate-400 uppercase bg-slate-800/50">
              <tr>
                <th className="px-6 py-3">Name</th>
                <th className="px-6 py-3">City</th>
                <th className="px-6 py-3">Category</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {businessesList.items.length === 0 ? (
                <tr><td colSpan={4} className="px-6 py-8 text-center text-slate-500">No businesses found.</td></tr>
              ) : (
                businessesList.items.map((biz: BusinessType) => (
                  <tr key={biz.id} className="hover:bg-slate-800/30 transition-colors cursor-pointer" onClick={() => setSelectedBusinessId(biz.id)}>
                    <td className="px-6 py-4 font-medium text-slate-200">{biz.name}</td>
                    <td className="px-6 py-4 text-slate-400">{biz.city || '-'}</td>
                    <td className="px-6 py-4 text-slate-400">{biz.primary_category || '-'}</td>
                    <td className="px-6 py-4 text-right">
                      <button className="text-emerald-400 hover:text-emerald-300 text-xs font-medium">View Details →</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // --- NEW BUSINESS FORM ---
  if (selectedBusinessId === 'NEW') {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <button onClick={() => setSelectedBusinessId(null)} className="text-slate-400 hover:text-white flex items-center gap-2 mb-4">
          <ArrowLeft className="h-4 w-4" /> Back to List
        </button>
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4 text-slate-100">Register New Business</h2>
          <RegisterBusinessForm onSuccess={(id) => { setSelectedBusinessId(id); }} onCancel={() => setSelectedBusinessId(null)} />
        </div>
      </div>
    );
  }

  // --- DETAIL VIEW ---
  if (!business) return null;
  const displayBusiness = isEditing ? editForm : business;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => { setSelectedBusinessId(null); setIsEditing(false); }} className="text-slate-400 hover:text-white flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <h1 className="text-2xl font-bold text-slate-100">{displayBusiness.name}</h1>
        </div>
        <div className="flex gap-2">
          {isEditing ? (
            <>
              <button onClick={() => { setIsEditing(false); setEditForm(business); }} className="px-3 py-1.5 text-sm bg-slate-800 hover:bg-slate-700 rounded-md text-slate-300 flex items-center gap-1">
                <X className="h-4 w-4" /> Cancel
              </button>
              <button onClick={() => updateMutation.mutate(editForm)} disabled={updateMutation.isPending} className="px-3 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-500 rounded-md text-white flex items-center gap-1 disabled:opacity-50">
                {updateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setIsEditing(true)} className="px-3 py-1.5 text-sm bg-slate-800 hover:bg-slate-700 rounded-md text-slate-300 flex items-center gap-1">
                <Edit className="h-4 w-4" /> Edit
              </button>
              <button onClick={() => { if(confirm('Are you sure? This will delete the business and all associated data.')) deleteMutation.mutate(); }} disabled={deleteMutation.isPending} className="px-3 py-1.5 text-sm bg-red-900/50 hover:bg-red-900 text-red-400 rounded-md flex items-center gap-1 disabled:opacity-50">
                {deleteMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Delete
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <DetailCard title="Core Info">
          <Field label="Name" value={displayBusiness.name} editable={isEditing} onChange={(v: string) => setEditForm(p => ({...p, name: v}))} />
          <Field label="Website" value={displayBusiness.website || '-'} icon={<Globe className="h-3 w-3" />} editable={isEditing} onChange={(v: string) => setEditForm(p => ({...p, website: v || null}))} />
          <Field label="Primary Category" value={displayBusiness.primary_category || '-'} editable={isEditing} onChange={(v: string) => setEditForm(p => ({...p, primary_category: v || null}))} />
          <Field label="City" value={displayBusiness.city || '-'} editable={isEditing} onChange={(v: string) => setEditForm(p => ({...p, city: v || null}))} />
          <Field label="Country Code" value={displayBusiness.country_code || '-'} editable={isEditing} onChange={(v: string) => setEditForm(p => ({...p, country_code: v || null}))} />
        </DetailCard>

        <DetailCard title="Location & Authority">
          <Field label="Latitude" value={displayBusiness.lat?.toString() || '-'} icon={<MapPin className="h-3 w-3" />} editable={isEditing} type="number" onChange={(v: string) => setEditForm(p => ({...p, lat: v ? parseFloat(v) : null}))} />
          <Field label="Longitude" value={displayBusiness.lng?.toString() || '-'} editable={isEditing} type="number" onChange={(v: string) => setEditForm(p => ({...p, lng: v ? parseFloat(v) : null}))} />
          <Field label="Google Rating" value={displayBusiness.google_rating?.toString() || '-'} editable={isEditing} type="number" onChange={(v: string) => setEditForm(p => ({...p, google_rating: v ? parseFloat(v) : null}))} />
          <Field label="Review Count" value={displayBusiness.google_review_count?.toString() || '-'} editable={isEditing} type="number" onChange={(v: string) => setEditForm(p => ({...p, google_review_count: v ? parseInt(v, 10) : null}))} />
          <Field label="GBP Verified" value={displayBusiness.gbp_verified ? 'Yes' : 'No'} editable={isEditing} type="checkbox" onChange={(v: string) => setEditForm((p: any) => ({...p, gbp_verified: v === 'true'}))} />
        </DetailCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-slate-900 border border-slate-800 rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4 text-slate-100 flex items-center gap-2">
            <FileJson className="h-5 w-5 text-blue-400" /> Latest Block Snapshots
          </h2>
          <div className="space-y-3">
            {BLOCKS.map((block) => (
              <BlockResultRow key={block} businessId={selectedBusinessId!} block={block} />
            ))}
          </div>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4 text-slate-100 flex items-center gap-2">
            <Activity className="h-5 w-5 text-emerald-400" /> Trigger Blocks
          </h2>
          <div className="grid grid-cols-1 gap-3">
            {BLOCKS.map((block) => (
              <button
                key={`trigger-${block}`}
                onClick={() => openTriggerModal(block)}
                className="flex items-center justify-between p-3 bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-medium rounded-md transition-colors"
              >
                <span>{block.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                <RefreshCw className="h-4 w-4" />
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
        <h2 className="text-lg font-semibold mb-4 text-slate-100">Tracked Keywords</h2>
        <form onSubmit={handleAddKeyword} className="flex gap-2 mb-4">
          <input
            type="text"
            value={newKeyword}
            onChange={(e) => setNewKeyword(e.target.value)}
            placeholder="Add a new keyword to track..."
            className="flex-1 bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
          <button type="submit" disabled={addKeywordMutation.isPending || !newKeyword.trim()} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-md transition-colors disabled:opacity-50 flex items-center gap-1">
            {addKeywordMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add
          </button>
        </form>
        <div className="space-y-2">
          {keywords?.items.filter(k => k.origin === 'tracked').map((kw: Keyword) => (
            <div key={kw.id} className="flex items-center justify-between p-3 bg-slate-800/30 rounded-md">
              <span className="text-slate-200 font-mono text-sm">{kw.keyword_text}</span>
              <button onClick={() => deleteKeywordMutation.mutate(kw.id)} className="text-slate-500 hover:text-red-400 transition-colors">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          {(!keywords?.items || keywords.items.filter(k => k.origin === 'tracked').length === 0) && (
            <p className="text-slate-500 text-sm text-center py-4">No tracked keywords yet.</p>
          )}
        </div>
      </div>

      {triggerModal.open && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-800">
              <h3 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
                <Settings className="h-5 w-5 text-emerald-400" />
                Configure: {triggerModal.block.replace('_', ' ').toUpperCase()}
              </h3>
              <button onClick={() => setTriggerModal({ open: false, block: '' })} className="text-slate-400 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {(triggerModal.block === 'grid_search' || triggerModal.block === 'perspective_search') && (
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-1">Keyword *</label>
                  <input
                    required
                    type="text"
                    value={triggerForm.keyword || ''}
                    onChange={(e) => setTriggerForm({ ...triggerForm, keyword: e.target.value })}
                    className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              )}

              {triggerModal.block === 'perspective_search' && triggerForm.points && (
                <div className="space-y-4">
                  <label className="block text-sm font-medium text-slate-400">Search Location (Point 1)</label>
                  <MapPicker 
                    lat={triggerForm.points[0].lat} 
                    lng={triggerForm.points[0].lng} 
                    onChange={(lat, lng) => setTriggerForm({ 
                      ...triggerForm, 
                      points: [{ ...triggerForm.points[0], lat, lng }] 
                    })} 
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Latitude</label>
                      <input
                        type="number"
                        step="any"
                        value={triggerForm.points[0].lat}
                        onChange={(e) => setTriggerForm({ ...triggerForm, points: [{ ...triggerForm.points[0], lat: parseFloat(e.target.value) || 0 }] })}
                        className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Longitude</label>
                      <input
                        type="number"
                        step="any"
                        value={triggerForm.points[0].lng}
                        onChange={(e) => setTriggerForm({ ...triggerForm, points: [{ ...triggerForm.points[0], lng: parseFloat(e.target.value) || 0 }] })}
                        className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200"
                      />
                    </div>
                  </div>
                </div>
              )}

              {triggerModal.block === 'grid_search' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-1">Grid Size (NxN)</label>
                    <input
                      type="number"
                      min={2}
                      max={9}
                      value={triggerForm.grid_size || 3}
                      onChange={(e) => setTriggerForm({ ...triggerForm, grid_size: parseInt(e.target.value) })}
                      className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-1">Radius (km)</label>
                    <input
                      type="number"
                      step="0.5"
                      min={0.5}
                      max={50}
                      value={triggerForm.radius_km || 3}
                      onChange={(e) => setTriggerForm({ ...triggerForm, radius_km: parseFloat(e.target.value) })}
                      className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200"
                    />
                  </div>
                </div>
              )}

              {triggerModal.block === 'keyword_generation' && (
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-1">Validate Top N (Optional)</label>
                  <input
                    type="number"
                    min={0}
                    max={50}
                    value={triggerForm.validate_top || 0}
                    onChange={(e) => setTriggerForm({ ...triggerForm, validate_top: parseInt(e.target.value) })}
                    className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200"
                  />
                </div>
              )}

              {triggerModal.block === 'seo_audit' && (
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-1">Page Cap</label>
                  <input
                    type="number"
                    min={1}
                    max={200}
                    value={triggerForm.page_cap || 50}
                    onChange={(e) => setTriggerForm({ ...triggerForm, page_cap: parseInt(e.target.value) })}
                    className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200"
                  />
                </div>
              )}

              {triggerModal.block === 'technical_health' && (
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-1">Strategy</label>
                  <select
                    value={triggerForm.strategy || 'mobile'}
                    onChange={(e) => setTriggerForm({ ...triggerForm, strategy: e.target.value })}
                    className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200"
                  >
                    <option value="mobile">Mobile</option>
                    <option value="desktop">Desktop</option>
                  </select>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-slate-800 flex justify-end gap-3 bg-slate-900/50">
              <button
                onClick={() => setTriggerModal({ open: false, block: '' })}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 font-medium rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={executeTrigger}
                disabled={triggerMutation.isPending || (['grid_search', 'perspective_search'].includes(triggerModal.block) && !triggerForm.keyword)}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-md transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {triggerMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Execute Block
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BlockResultRow({ businessId, block }: { businessId: string; block: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['snapshot', businessId, block],
    queryFn: () => getLatestSnapshot(businessId, block),
    retry: false,
  });

  const [expanded, setExpanded] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center justify-between p-3 bg-slate-800/30 rounded-md animate-pulse">
        <div className="h-4 w-32 bg-slate-700 rounded" />
        <div className="h-4 w-20 bg-slate-700 rounded" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex items-center justify-between p-3 bg-slate-800/10 rounded-md border border-slate-800/50">
        <span className="text-sm text-slate-500 font-mono">{block}</span>
        <span className="text-xs text-orange-500/80 italic">
          {error ? 'Failed to load' : 'No data yet'}
        </span>
      </div>
    );
  }

  const hasData = data.has_data;
  const capturedAt = data.captured_at || data.freshness;

  const renderBlockContent = () => {
    switch (block) {
      case 'seo_audit': return <SeoAuditRenderer data={data} />;
      case 'keyword_search': return <KeywordSearchRenderer data={data} />;
      case 'keyword_generation': return <KeywordGenerationRenderer data={data} />;
      case 'grid_search': return <GridSearchRenderer data={data} />;
      case 'perspective_search': return <PerspectiveSearchRenderer data={data} />;
      case 'technical_health': return <TechnicalHealthRenderer data={data} />;
      default: return <GenericBlockRenderer data={data} />;
    }
  };

  return (
    <div className="bg-slate-800/30 rounded-md border border-slate-700/50 overflow-hidden">
      <button 
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 hover:bg-slate-800/50 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <span className={`w-2 h-2 rounded-full ${hasData ? 'bg-emerald-400' : 'bg-slate-500'}`} />
          <span className="text-sm font-medium text-slate-200 font-mono capitalize">{block.replace('_', ' ')}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500 hidden sm:inline">
            {capturedAt ? new Date(capturedAt).toLocaleString() : 'Unknown'}
          </span>
          {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
        </div>
      </button>
      
      {expanded && (
        <div className="p-5 bg-slate-950/90 border-t border-slate-800">
          {renderBlockContent()}
        </div>
      )}
    </div>
  );
}

function DetailCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-lg p-6 space-y-4">
      <h3 className="text-md font-semibold text-slate-300 border-b border-slate-800 pb-2">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, value, icon, editable, type = 'text', onChange }: any) {
  if (editable) {
    if (type === 'checkbox') {
      return (
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-400">{label}</span>
          <select value={value ? 'true' : 'false'} onChange={(e) => onChange(e.target.value)} className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm text-slate-200">
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-1">
        <span className="text-xs text-slate-500">{label}</span>
        <input 
          type={type} 
          value={value === null ? '' : value} 
          onChange={(e) => onChange(e.target.value || null)} 
          className="bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-sm text-slate-200 focus:outline-none focus:ring-1 focus:ring-emerald-500"
        />
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-slate-400 flex items-center gap-2">{icon && icon}{label}</span>
      <span className="text-sm font-medium text-slate-200">{value}</span>
    </div>
  );
}

function RegisterBusinessForm({ onSuccess, onCancel }: { onSuccess: (id: string) => void; onCancel: () => void }) {
  const [form, setForm] = useState({ name: '', website: '', lat: '', lng: '', city: '', country_code: '' });
  const mutation = useMutation({
    mutationFn: () => registerBusiness({
      ...form,
      lat: form.lat ? parseFloat(form.lat) : null,
      lng: form.lng ? parseFloat(form.lng) : null,
    }),
    onSuccess: (data) => {
      onSuccess(data.id);
      setForm({ name: '', website: '', lat: '', lng: '', city: '', country_code: '' });
    }
  });

  return (
    <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <input required placeholder="Business Name *" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-slate-200" />
        <input placeholder="Website" value={form.website} onChange={e => setForm({...form, website: e.target.value})} className="bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-slate-200" />
        <div className="col-span-2">
          <label className="block text-xs text-slate-500 mb-1">Business Location (Click on map)</label>
          <MapPicker 
            lat={form.lat ? parseFloat(form.lat) : null} 
            lng={form.lng ? parseFloat(form.lng) : null} 
            onChange={(lat, lng) => setForm({...form, lat: lat.toString(), lng: lng.toString()})} 
            height="200px"
          />
        </div>
        <input placeholder="Latitude" type="number" step="any" value={form.lat} onChange={e => setForm({...form, lat: e.target.value})} className="bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-slate-200" />
        <input placeholder="Longitude" type="number" step="any" value={form.lng} onChange={e => setForm({...form, lng: e.target.value})} className="bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-slate-200" />
        <input placeholder="City" value={form.city} onChange={e => setForm({...form, city: e.target.value})} className="bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-slate-200" />
        <input placeholder="Country Code (e.g., IN)" value={form.country_code} onChange={e => setForm({...form, country_code: e.target.value})} className="bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-slate-200" />
      </div>
      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onCancel} className="flex-1 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 font-medium rounded-md transition-colors">
          Cancel
        </button>
        <button type="submit" disabled={mutation.isPending || !form.name} className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-md transition-colors disabled:opacity-50">
          {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : 'Register Business'}
        </button>
      </div>
    </form>
  );
}
